import React, { useState, useEffect } from "react";
import {
  Grid,
  Segment,
  Dimmer,
  Loader,
  Message,
  Button,
} from "semantic-ui-react";
import { useAuth } from "../../../hooks";
import { Dashboard } from "../../../api";
import { StatCard } from "../../../components/Admin/Dashboard/StatCard";
import {
  DashboardHeader,
  TodaySchedule,
  AttendanceSummary,
  NextPayment,
  AcademicPerformance,
  NotificationsList,
  CoursesGrid,
} from "../../../components/Student/Dashboard";
import "./Dashboard.scss";

const dashboardController = new Dashboard();

export function DashboardPage() {
  const { accessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [overview, setOverview] = useState(null);
  const [academic, setAcademic] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [learning, setLearning] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, [accessToken]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const results = await Promise.allSettled([
        dashboardController.getStudentOverview(),
        dashboardController.getStudentAcademic(),
        dashboardController.getStudentAttendance(),
        dashboardController.getStudentFinancial(),
        dashboardController.getStudentSchedule(),
        dashboardController.getStudentNotifications(),
        dashboardController.getStudentLearning(),
      ]);

      if (results[0].status === "fulfilled") setOverview(results[0].value);
      if (results[1].status === "fulfilled") setAcademic(results[1].value);
      if (results[2].status === "fulfilled") setAttendance(results[2].value);
      if (results[3].status === "fulfilled") setFinancial(results[3].value);
      if (results[4].status === "fulfilled") setSchedule(results[4].value);
      if (results[5].status === "fulfilled") setNotifications(results[5].value);
      if (results[6].status === "fulfilled") setLearning(results[6].value);

      if (results[0].status === "rejected") {
        console.error("Failed to load overview:", results[0].reason);
      }
    } catch (err) {
      console.error("Dashboard error:", err);
      setError("Ocurrió un error al cargar el dashboard.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboard();
  };

  // Determine current day for schedule
  const days = [
    "Domingo",
    "Lunes",
    "Martes",
    "Miércoles",
    "Jueves",
    "Viernes",
    "Sábado",
  ];
  const currentDay = days[new Date().getDay()];

  if (loading && !overview) {
    return (
      <div className="student-dashboard">
        <Segment basic>
          <Dimmer active inverted>
            <Loader size="large">Cargando Dashboard...</Loader>
          </Dimmer>
        </Segment>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-dashboard">
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
          <Button onClick={loadDashboard}>Reintentar</Button>
        </Message>
      </div>
    );
  }

  return (
    <div className="student-dashboard">
      {/* Welcome Header */}
      <DashboardHeader
        studentName={overview?.student?.name}
        gradeName={overview?.student?.gradeName}
        academicCycle={overview?.student?.academicCycle}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      {/* Key Metrics */}
      <Grid stackable columns={4} className="metrics-grid">
        <Grid.Column>
          <StatCard
            title="Promedio General"
            value={academic?.summary?.overallAverage || "-"}
            icon="star"
            color="#f59e0b"
            trend={
              academic?.summary?.subjectsAtRisk > 0
                ? {
                    type: "down",
                    value: academic?.summary?.subjectsAtRisk,
                    label: "en riesgo",
                  }
                : null
            }
          />
        </Grid.Column>
        <Grid.Column>
          <StatCard
            title="Asistencia"
            value={`${attendance?.summary?.attendanceRate || 0}%`}
            icon="calendar check"
            color="#10b981"
            trend={{
              type: "up",
              value: attendance?.summary?.presentDays || 0,
              label: "días",
            }}
          />
        </Grid.Column>
        <Grid.Column>
          <StatCard
            title="Pagos Pendientes"
            value={financial?.summary?.pendingPayments || 0}
            icon="credit card"
            color={
              financial?.summary?.pendingPayments > 0 ? "#ef4444" : "#10b981"
            }
          />
        </Grid.Column>
        <Grid.Column>
          <StatCard
            title="Cursos"
            value={academic?.summary?.totalCourses || 0}
            icon="book"
            color="#3b82f6"
          />
        </Grid.Column>
      </Grid>

      {/* Main Content Grid */}
      <Grid className="main-content-grid" stackable>
        {/* Left Column */}
        <Grid.Column width={6}>
          {/* Today's Schedule */}
          <TodaySchedule schedule={schedule} currentDay={currentDay} />

          {/* Attendance Summary */}
          <AttendanceSummary attendance={attendance} />

          {/* Next Payment */}
          <NextPayment paymentData={financial?.nextPaymentDue} />
        </Grid.Column>

        {/* Right Column */}
        <Grid.Column width={10}>
          {/* Academic Performance Chart */}
          <AcademicPerformance courses={academic?.courses} />

          {/* Notifications */}
          <NotificationsList notifications={notifications} />

          {/* All Courses */}
          <CoursesGrid courses={academic?.courses} />
        </Grid.Column>
      </Grid>
    </div>
  );
}

export { DashboardPage as Dashboard };
